import type { CopyFileRequestModel } from "@xlabmedical/medic-api";

const COPY_FILE_CONFLICT_STATUS = 409;
const COPY_FILE_RETRY_DELAY_MS = 2000;
const COPY_FILE_MAX_RETRIES = 15;

type CopyFileBetweenProjectsFn = (model: CopyFileRequestModel) => Promise<number | undefined>;
type RetryReason = "status_409" | "processing_message";

export interface CopyRetryAttempt {
    attempt: number;
    reason: RetryReason;
}

export interface CopyWithRetryOptions {
    onRetryAttempt?: (info: CopyRetryAttempt) => void;
}

const waitFor = (ms: number) =>
    new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms);
    });

const getObjectValue = (value: unknown, key: string): unknown => {
    if (typeof value !== "object" || value === null) {
        return undefined;
    }

    return (value as Record<string, unknown>)[key];
};

const getStatusValue = (value: unknown): number | undefined => {
    const status = getObjectValue(value, "status");
    return typeof status === "number" ? status : undefined;
};

const getMessageValue = (value: unknown): string => {
    const message = getObjectValue(value, "message");
    return typeof message === "string" ? message : "";
};

const getApiErrorStatus = (err: unknown): number | undefined => {
    const cause = getObjectValue(err, "cause");

    const status = [
        getStatusValue(getObjectValue(err, "response")),
        getStatusValue(err),
        getStatusValue(getObjectValue(err, "request")),
        getStatusValue(getObjectValue(cause, "response")),
        getStatusValue(cause),
        getStatusValue(getObjectValue(cause, "request")),
    ].find((candidate): candidate is number => typeof candidate === "number");

    if (status !== undefined) {
        return status;
    }

    const rawMessage = `${String(getObjectValue(err, "message") ?? "")} ${String(getObjectValue(cause, "message") ?? "")}`;
    const statusMatch = rawMessage.match(/\b\d{3}\b/);
    return statusMatch ? Number(statusMatch[0]) : undefined;
};

const getErrorMessageCandidates = (err: unknown): string[] => {
    const cause = getObjectValue(err, "cause");
    const response = getObjectValue(err, "response");
    const causeResponse = getObjectValue(cause, "response");

    const responseData = getObjectValue(response, "data");
    const causeResponseData = getObjectValue(causeResponse, "data");

    const candidates = [
        getMessageValue(err),
        getMessageValue(cause),
        getMessageValue(responseData),
        getMessageValue(causeResponseData),
        typeof responseData === "string" ? responseData : "",
        typeof causeResponseData === "string" ? causeResponseData : "",
    ];

    return candidates.map((candidate) => candidate.trim()).filter((candidate) => candidate.length > 0);
};

const hasProcessingConflictMessage = (err: unknown): boolean => {
    const normalizedMessage = getErrorMessageCandidates(err).join(" ").toLowerCase();
    if (!normalizedMessage) {
        return false;
    }

    return (
        normalizedMessage.includes("revision file is being processed") ||
        normalizedMessage.includes("project file is being processed") ||
        (normalizedMessage.includes("being processed") && normalizedMessage.includes("try again later"))
    );
};

export const getCopyErrorMessage = (err: unknown): string => {
    const messageCandidates = getErrorMessageCandidates(err);
    if (messageCandidates.length > 0) {
        return messageCandidates[0];
    }

    if (err instanceof Error && err.message) {
        return err.message;
    }

    if (typeof err === "string" && err.trim().length > 0) {
        return err;
    }

    const message = getObjectValue(err, "message");
    if (typeof message === "string" && message.trim().length > 0) {
        return message;
    }

    return "Unknown error";
};

export const copyFileBetweenProjectsWithRetry = async (
    copyModel: CopyFileRequestModel,
    copyFileBetweenProjects: CopyFileBetweenProjectsFn,
    options?: CopyWithRetryOptions,
): Promise<number> => {
    for (let retryCount = 0; ; retryCount += 1) {
        try {
            const copiedFileId = await copyFileBetweenProjects(copyModel);

            if (copiedFileId && copiedFileId > 0) {
                return copiedFileId;
            }

            throw new Error("copyFileBetweenProjects returned an invalid fileId");
        } catch (copyError) {
            const status = getApiErrorStatus(copyError);
            const hasConflictStatus = status === COPY_FILE_CONFLICT_STATUS;
            const hasConflictMessage = hasProcessingConflictMessage(copyError);
            const canRetry = (hasConflictStatus || hasConflictMessage) && retryCount < COPY_FILE_MAX_RETRIES - 1;

            if (!canRetry) {
                throw copyError;
            }

            options?.onRetryAttempt?.({
                attempt: retryCount + 1,
                reason: hasConflictStatus ? "status_409" : "processing_message",
            });
            await waitFor(COPY_FILE_RETRY_DELAY_MS);
        }
    }
};
